/**
 * buildIdentity — the RUNTIME single source of truth for what build this bundle
 * is. Reads the compile-time defines wired in apps/command/vite.config.ts
 * (whose build-time half is apps/command/src/buildIdentity/resolveBuildIdentity.ts).
 *
 * WHY THIS EXISTS: three separate places each re-implemented
 *
 *     typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : 'dev'
 *
 * — scoutLog.getBuildHash, apps/command buildInfo.getBuildInfo, and the query
 * persister's cache buster. Three copies of one rule is three chances to drift,
 * and all three inherited the same lie: the production image has no git binary,
 * so vite's `catch { return 'dev' }` fired on every prod build and every prod
 * bundle reported itself as 'dev' while the DEV SERVER reported a real SHA. The
 * labels were inverted, and that fog blocked the diagnosis of a live outage
 * losing a rep's visit reports. One rule, one home (CODING-STANDARDS §2).
 *
 * The `typeof` guards stay: a raw `tsc`/vitest run has no vite inject, so the
 * defines genuinely do not exist and reading them bare throws ReferenceError.
 * What changes is the ANSWER — UNRESOLVED, never a plausible-looking 'dev'.
 */

declare const __BUILD_HASH__: string
declare const __APP_BUILD__: string
declare const __BUILD_TIME__: string

/**
 * The single sentinel for "this bundle has no verifiable identity". Kept
 * byte-identical to the build-time constant in
 * apps/command/src/buildIdentity/resolveBuildIdentity.ts — one vocabulary end to end.
 */
export const BUILD_IDENTITY_UNRESOLVED = 'unresolved'

export interface BuildIdentity {
  /** Git short SHA the bundle was built from, or UNRESOLVED. Never 'dev'. */
  commit: string
  /** Cloud Build id ($SHORT_SHA-$BUILD_ID) — distinguishes rebuilds — or UNRESOLVED. */
  build: string
  /** ISO timestamp the bundle was built, or UNRESOLVED. */
  builtAt: string
  /** False when the bundle cannot say which build it is. Queryable in telemetry. */
  resolved: boolean
}

/**
 * Read one compile-time define. `read` is a thunk because the define is
 * substituted textually at build time — it must be referenced inside a `typeof`
 * guard, not passed as a value.
 */
function readDefine(read: () => string): string {
  const value = read()
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : BUILD_IDENTITY_UNRESOLVED
}

/** What this bundle can prove about its own origin. */
export function getBuildIdentity(): BuildIdentity {
  const commit = readDefine(() => (typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : ''))
  const build = readDefine(() => (typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : ''))
  const builtAt = readDefine(() => (typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : ''))

  return {
    commit,
    build,
    builtAt,
    // Either identity is enough to trace a bundle back to a build.
    resolved: commit !== BUILD_IDENTITY_UNRESOLVED || build !== BUILD_IDENTITY_UNRESOLVED,
  }
}
